import { Controller, Path as RHFPath, useController, useFormContext, useFormState } from "react-hook-form";

import E164PhoneNumberInput from "@/components/E164PhoneNumberInput";
import Select from "@/components/Select";
import TextField from "@/components/TextField";

import { type CreateFacilityFormValues } from "../../_utils/createFacilityForm";

export function optionsWithEmpty(enumValues: { name: string; description: string }[] = []) {
  return [
    { value: "", displayText: "--" },
    ...enumValues.map((option) => ({
      value: option.name,
      label: option.description
    }))
  ];
}

export function FormTextField({
  name,
  type,
  ...props
}: Omit<
  React.ComponentProps<typeof TextField>,
  "name" | "value" | "onChangeText" | "disabled" | "error" | "errorText"
> & {
  name: RHFPath<CreateFacilityFormValues>;
}) {
  const { control } = useFormContext<CreateFacilityFormValues>();
  const { field, fieldState } = useController({ control, name });
  const { isSubmitting } = useFormState({ control });
  const fieldValue = field.value;
  const value =
    typeof fieldValue === "number"
      ? Number.isNaN(fieldValue)
        ? ""
        : fieldValue.toString()
      : typeof fieldValue === "string"
        ? fieldValue
        : "";

  return (
    <TextField
      {...props}
      name={field.name}
      ref={field.ref}
      type={type}
      value={value}
      onBlur={field.onBlur}
      onChangeText={(nextValue) => {
        field.onChange(type === "number" ? (nextValue === "" ? null : Number(nextValue)) : nextValue);
      }}
      disabled={isSubmitting}
      error={!!fieldState.error}
      errorText={fieldState.error?.message}
    />
  );
}

export function PhoneField({
  name,
  label,
  required
}: {
  name: "phone" | "fax" | "golfShopPhone";
  label: string;
  required?: boolean;
}) {
  const form = useFormContext<CreateFacilityFormValues>();
  const {
    formState: { errors, isSubmitting },
    watch,
    setValue,
    setError,
    clearErrors
  } = form;

  return (
    <E164PhoneNumberInput
      value={watch(name) || ""}
      label={required ? `${label} *` : label}
      onChange={(value) => setValue(name, value, { shouldDirty: true, shouldValidate: true })}
      onError={(error) => (error ? setError(name, { message: error }) : clearErrors(name))}
      disabled={isSubmitting}
      error={!!errors[name]}
      errorText={errors[name]?.message}
    />
  );
}

export function EnumSelectField({
  name,
  label,
  options,
  required
}: {
  name: RHFPath<CreateFacilityFormValues>;
  label: string;
  options: { value: string; displayText?: string; label?: string }[];
  required?: boolean;
}) {
  const { control } = useFormContext<CreateFacilityFormValues>();
  const { isSubmitting } = useFormState({ control });

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState }) => (
        <Select
          value={(value as string) || ""}
          label={label}
          onSelect={onChange}
          options={options}
          disabled={isSubmitting}
          required={required}
          error={!!fieldState.error}
          errorText={fieldState.error?.message}
        />
      )}
    />
  );
}
